import { useEffect, useState } from 'react';
import styles from './ConfirmModal.module.css';

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (inputValue?: string) => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmButtonVariant?: 'danger' | 'primary' | 'warning';
  isLoading?: boolean;
  showInput?: boolean;
  inputLabel?: string;
  inputPlaceholder?: string;
  inputRequired?: boolean;
  inputType?: 'text' | 'number' | 'textarea';
  infoHint?: string;
  incidentNote?: string;
}

export function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  confirmButtonVariant = 'primary',
  isLoading = false,
  showInput = false,
  inputLabel = '',
  inputPlaceholder = '',
  inputRequired = false,
  inputType = 'textarea',
  infoHint,
  incidentNote,
}: ConfirmModalProps) {
  const [inputValue, setInputValue] = useState('');

  // Reset input cuando se abre/cierra el modal
  useEffect(() => {
    if (!isOpen) {
      setInputValue('');
    }
  }, [isOpen]);

  // Cerrar con ESC
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isLoading) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, isLoading, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  const handleConfirm = () => {
    if (isLoading) return;

    if (showInput && inputRequired && !inputValue.trim()) {
      return; // No confirmar si el input es requerido y está vacío
    }

    onConfirm(showInput ? inputValue : undefined);
  };

  const canConfirm = !showInput || !inputRequired || inputValue.trim().length > 0;

  return (
    <div className={styles.overlay} onClick={handleBackdropClick}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
          <button
            className={styles.closeButton}
            onClick={onClose}
            disabled={isLoading}
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <div className={styles.content}>
          <p className={styles.message}>{message}</p>
          {infoHint && (
            <div className={styles.infoHint}>
              <span className={styles.infoHintIcon}>ℹ️</span>
              <span>{infoHint}</span>
            </div>
          )}
          {incidentNote && (
            <div className={styles.incidentNote}>
              <span className={styles.incidentNoteIcon}>⚠️</span>
              <span>{incidentNote}</span>
            </div>
          )}
          {showInput && (
            <div className={styles.inputContainer}>
              <label htmlFor="modal-input" className={styles.inputLabel}>
                {inputLabel}
                {inputRequired && <span className={styles.required}> *</span>}
              </label>
              {inputType === 'textarea' ? (
                <textarea
                  id="modal-input"
                  className={styles.input}
                  placeholder={inputPlaceholder}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  disabled={isLoading}
                  rows={3}
                />
              ) : (
                <input
                  type={inputType}
                  id="modal-input"
                  className={styles.input}
                  placeholder={inputPlaceholder}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  disabled={isLoading}
                  style={{ height: '40px' }}
                />
              )}
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <button
            className={`${styles.button} ${styles.cancelButton}`}
            onClick={onClose}
            disabled={isLoading}
          >
            {cancelText}
          </button>
          <button
            className={`${styles.button} ${styles[`confirm${confirmButtonVariant}`]}`}
            onClick={handleConfirm}
            disabled={isLoading || !canConfirm}
          >
            {isLoading ? 'Procesando...' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

